import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { z } from 'zod'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '@/redux'
import { Form } from '@/components/ui/form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { getFormProps } from '@/helpers/formHelpers'
import { TextInputField } from '@/components/custom/InputField/TextInputField'
import { createDashboard, renameDashboard } from '@/services/dashboardsService'
import { dashboardsActions } from '@/redux/slice/dashboardsSlice'
import type { DashboardItem } from '@/types/dashboard'

const formSchema = z.object({
  name: z.string().trim().min(1, { message: 'Dashboard name is required.' }).max(255, {
    message: 'Dashboard name must be 255 characters or fewer.'
  })
})

type DashboardModalMode = 'create' | 'rename'

interface CreateOrUpdateDashboardModalProps {
  isOpen: boolean
  onClose: () => void
  mode: DashboardModalMode
  /** Required when mode="rename". */
  dashboard?: DashboardItem
  /** Called after a successful create/rename, before closing the modal. */
  onSuccess?: (dashboard: DashboardItem) => void
}

const getDefaultName = (mode: DashboardModalMode, dashboard?: DashboardItem) => {
  if (mode === 'rename') {
    return dashboard?.name ?? ''
  }
  return ''
}

export const CreateOrUpdateDashboardModal = ({
  isOpen,
  onClose,
  mode,
  dashboard,
  onSuccess,
}: CreateOrUpdateDashboardModalProps) => {
  const formId = 'create-or-update-dashboard-form'
  const dispatch: AppDispatch = useDispatch()
  const { token } = useSelector((state: RootState) => state.user)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: getDefaultName(mode, dashboard)
    }
  })
  const nameValue = form.watch('name')
  const formProps = getFormProps(form, formSchema)

  useEffect(() => {
    if (!isOpen) return
    form.setValue('name', getDefaultName(mode, dashboard))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, dashboard?.id])

  const onFormSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!token) {
      return
    }

    if (mode === 'rename' && !dashboard) {
      return
    }

    const trimmedName = values.name.trim()

    if (mode === 'rename') {
      const currentName = (dashboard!.name ?? '').trim()
      if (trimmedName === currentName) {
        onClose()
        return
      }
    }

    const resp =
      mode === 'create'
        ? await dispatch(createDashboard(token, trimmedName))
        : await dispatch(renameDashboard(token, dashboard!.id, trimmedName))
    if (resp.data) {
      if (mode === 'create') {
        // Set selectedDashboard so DashboardChatService stale-guards won't discard
        // chat/session responses when we immediately navigate to /dashboards/new.
        dispatch(dashboardsActions.setSelectedDashboard(resp.data))
      }

      onSuccess?.(resp.data)
      onClose()
    }
  }

  const title = mode === 'rename' ? 'Rename Dashboard' : 'New Dashboard'
  const description =
    mode === 'rename'
      ? 'Update your dashboard name.'
      : 'Give your dashboard a name to get started.'
  const submitLabel = mode === 'rename' ? 'Rename Dashboard' : 'Create Dashboard'

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <AlertDialogContent className="max-w-2xl">
        <div className="flex flex-row items-start justify-between mb-4">
          <div className="flex flex-col items-start">
            <AlertDialogTitle className="text-primary font-semibold text-xl leading-5 mb-2">
              {title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-secondary text-sm text-left font-normal">
              {description}
            </AlertDialogDescription>
          </div>

          <div className="flex items-center">
            <AlertDialogCancel className="mx-4">Cancel</AlertDialogCancel>
            <AlertDialogAction
              form={formId}
              type="submit"
              disabled={
                (token?.length ?? 0) === 0 ||
                nameValue.trim().length === 0 ||
                (mode === 'rename' && !dashboard)
              }
            >
              {submitLabel}
            </AlertDialogAction>
          </div>
        </div>

        <Form {...form}>
          <form
            id={formId}
            className="space-y-6 flex flex-col items-start mt-4"
            onSubmit={form.handleSubmit(onFormSubmit)}
          >
            <TextInputField
              name="name"
              label="Dashboard Name"
              error={!!form.formState.errors.name}
              additionalClassName="w-[460px] focus-visible:ring-0 focus-visible:ring-offset-0"
              {...formProps}
            />
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
